// frontend/src/widgets/OrgCalendarWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Castle Traffic Ltd
// Created:      2025-09-03
// Updated:      2026-09-09 14:37 BST
//
// Description:
//   Lightweight organisational/resource availability calendar view (list style).
//   For the selected date range:
//   - Loads active resources
//   - Loads each resource's assignments and PTO/unavailability
//   - Displays grouped by resource with simple chips
//   Uses:
//     GET /api/resources/?active=true
//     GET /api/resources/{id}/assignments
//     GET /api/resources/{id}/unavailability
//   (Client-side filters by date range until server filters are available.)
//
// Mobile Responsiveness (2026-09-09):
//   - Keeps the existing resource/assignment/unavailability API behaviour unchanged.
//   - Date controls stack on mobile and remain a single row on desktop.
//   - Refresh becomes a full-width touch target on mobile.
//   - Each resource remains a single grouped card containing assignments and
//     PTO/unavailability.
//   - Mobile shows one complete resource card per vertical scroll/snap.
//   - Dynamic mobile growth uses the proven Vision pattern:
//       restore previous host sizing;
//       clear temporary viewport/root sizing;
//       measure the natural first resource card;
//       set the records viewport to exactly one complete card;
//       wait a second animation frame;
//       resize the first width-matching fixed-height mobile host.
//   - ResizeObserver watches the first resource card; state/date/loading changes
//     retrigger sizing.
//   - Desktop keeps the existing natural stacked resource list.
//
// Operational Notes:
//   - The unavailability endpoint is the source of PTO/unavailability records.
//   - This widget displays availability records; it does not create/edit them.
//   - Public-holiday entries are displayed when they are represented by the
//     existing unavailability data returned by the backend.
//   - No new API endpoint or duplicate mobile render tree is introduced.

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";

const within = (start, end, from, to) => {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const f = new Date(from).getTime();
  const t = new Date(to).getTime();

  // overlap if s <= t && e >= f
  return s <= t && e >= f;
};

const chip = (bg, border, color = "#111") => ({
  display: "inline-block",
  maxWidth: "100%",
  padding: "4px 8px",
  borderRadius: 12,
  border: `1px solid ${border}`,
  background: bg,
  color,
  fontSize: 12,
  lineHeight: "16px",
  fontWeight: 700,
  marginRight: 6,
  marginBottom: 4,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  boxSizing: "border-box",
});

const OrgCalendarWidget = () => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const recordsViewportRef = useRef(null);
  const firstResourceCardRef = useRef(null);

  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });

  const [resources, setResources] = useState([]);
  const [items, setItems] = useState({}); // {resourceId: {assignments:[], pto:[]}}
  const [loading, setLoading] = useState(false);

  const input = useMemo(
    () => ({
      padding: "8px",
      borderRadius: 6,
      border: "1px solid #ccc",
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      height: 44,
      fontFamily: theme.font_body,
    }),
    [theme.font_body]
  );

  const load = async () => {
    setLoading(true);

    try {
      const r = await api.get("/resources/", {
        params: {
          active: true,
          limit: 100,
        },
      });

      const list = Array.isArray(r.data) ? r.data : [];
      setResources(list);

      const out = {};

      for (const res of list) {
        try {
          const [a, u] = await Promise.all([
            api.get(`/resources/${res.id}/assignments`),
            api.get(`/resources/${res.id}/unavailability`),
          ]);

          out[res.id] = {
            assignments: Array.isArray(a.data) ? a.data : [],
            pto: Array.isArray(u.data) ? u.data : [],
          };
        } catch (e) {
          out[res.id] = {
            assignments: [],
            pto: [],
          };
        }
      }

      setItems(out);
    } catch (e) {
      console.error(
        "Load org calendar failed:",
        e?.response?.data || e?.message || e
      );

      setResources([]);
      setItems({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fromIso = `${from}T00:00:00`;
  const toIso = `${to}T23:59:59`;

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = recordsViewportRef.current;
    const firstResourceCard = firstResourceCardRef.current;

    if (!root || !viewport) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstCard = firstResourceCardRef.current;

        if (currentFirstCard) {
          const cardHeight = Math.ceil(
            Math.max(
              currentFirstCard.getBoundingClientRect().height || 0,
              currentFirstCard.scrollHeight || 0
            )
          );

          if (Number.isFinite(cardHeight) && cardHeight > 0) {
            viewport.style.height = `${cardHeight}px`;
            viewport.style.minHeight = `${cardHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstResourceCard) {
      resizeObserver.observe(firstResourceCard);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      viewport.style.height = "";
      viewport.style.minHeight = "";
      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    from,
    items,
    loading,
    resources.length,
    to,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-org-calendar-records {
            overflow-y: auto;
            overflow-x: hidden;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-org-calendar-records::-webkit-scrollbar {
            display: none;
          }

          .vx-org-calendar-resource-card {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>

      <div className="mb-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_1fr_auto]">
        <input
          style={input}
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="Availability from date"
        />

        <input
          style={input}
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="Availability to date"
        />

        <button
          type="button"
          onClick={load}
          className="min-h-[44px] w-full md:w-auto"
          style={{
            background: "var(--btn-general-normal, #222)",
            color: "#fff",
            padding: "8px 12px",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {resources.length === 0 && !loading ? (
        <div style={{ color: "#666" }}>
          No resources.
        </div>
      ) : null}

      <div
        ref={recordsViewportRef}
        className="vx-org-calendar-records flex flex-col gap-3 overflow-visible md:h-auto md:overflow-visible"
      >
        {resources.map((resource, index) => {
          const data = items[resource.id] || {
            assignments: [],
            pto: [],
          };

          const assignments = data.assignments.filter((assignment) =>
            assignment.starts_at && assignment.ends_at
              ? within(
                  assignment.starts_at,
                  assignment.ends_at,
                  fromIso,
                  toIso
                )
              : true
          );

          const unavailability = data.pto.filter((entry) =>
            entry.starts_at && entry.ends_at
              ? within(
                  entry.starts_at,
                  entry.ends_at,
                  fromIso,
                  toIso
                )
              : true
          );

          return (
            <div
              key={resource.id}
              ref={index === 0 ? firstResourceCardRef : null}
              className="vx-org-calendar-resource-card min-w-0"
              style={{
                border: "1px solid #eee",
                borderRadius: 8,
                padding: 10,
                boxSizing: "border-box",
                overflowWrap: "anywhere",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  marginBottom: 8,
                  overflowWrap: "anywhere",
                }}
              >
                {resource.first_name} {resource.last_name}{" "}
                <span style={{ color: "#666" }}>
                  #{resource.id}
                </span>
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                Assignments
              </div>

              <div style={{ marginBottom: 8 }}>
                {assignments.length === 0 ? (
                  <span style={{ color: "#666" }}>
                    No assignments.
                  </span>
                ) : (
                  assignments.map((assignment) => (
                    <span
                      key={assignment.id}
                      style={chip("#eef6ff", "#90caf9")}
                      title={`${new Date(
                        assignment.starts_at
                      ).toLocaleString()} → ${new Date(
                        assignment.ends_at
                      ).toLocaleString()}`}
                    >
                      Job {assignment.job_id} •{" "}
                      {assignment.role || "assigned"}
                    </span>
                  ))
                )}
              </div>

              <div
                style={{
                  fontSize: 11,
                  color: "#666",
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                PTO / Unavailability
              </div>

              <div>
                {unavailability.length === 0 ? (
                  <span style={{ color: "#666" }}>
                    No PTO / unavailability.
                  </span>
                ) : (
                  unavailability.map((entry) => (
                    <span
                      key={entry.id}
                      style={chip("#ffefef", "#f48fb1")}
                      title={`${new Date(
                        entry.starts_at
                      ).toLocaleString()} → ${new Date(
                        entry.ends_at
                      ).toLocaleString()}`}
                    >
                      {entry.reason?.toUpperCase() || "PTO"}
                    </span>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrgCalendarWidget;
